import { MessageSquareIcon, MonitorIcon, PaperclipIcon } from 'lucide-react'
import { type FC, memo, type ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

import { ChatInputAttachButtonDisabled } from './chat-input-attach-button-disabled.atom'

export interface ChatInputAttachmentMenuProps {
  onSelectFromComputer: () => void
  onSelectFromChats: () => void
  isDisabled?: boolean
  disabledReason?: string
  className?: string
}

const ChatInputAttachmentMenuBase: FC<ChatInputAttachmentMenuProps> = ({
  onSelectFromComputer,
  onSelectFromChats,
  isDisabled = false,
  disabledReason,
  className,
}): ReactNode => {
  if (disabledReason) {
    return <ChatInputAttachButtonDisabled reason={disabledReason} className={className} />
  }

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={isDisabled}
              className={cn('chat-input-attach-button gap-1.5', className)}
              aria-label="Add attachment"
            >
              <PaperclipIcon className="size-4" />
              <span className="hidden sm:inline">Attach</span>
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">Add attachment</TooltipContent>
      </Tooltip>

      <DropdownMenuContent align="start" className="w-48">
        <DropdownMenuItem onClick={onSelectFromComputer}>
          <MonitorIcon className="size-4" />
          <span>Your computer</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onSelectFromChats}>
          <MessageSquareIcon className="size-4" />
          <span>Other chats</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const ChatInputAttachmentMenu = memo(ChatInputAttachmentMenuBase)

ChatInputAttachmentMenu.displayName = 'ChatInputAttachmentMenu'
